import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import i18n from '../i18n';

const AuthContext = createContext();

// Начальное состояние
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: null,
};

// Reducer для управления состоянием
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
      };
    
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Проверка токена при загрузке приложения
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token') || Cookies.get('token');
      
      if (!token) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      try {
        const response = await authAPI.getProfile();
        const user = response.data.data.user;
        
        console.log('CheckAuth response user:', user);
        console.log('CheckAuth user role:', user?.role);
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user,
            token,
          },
        });
      } catch (error) {
        // Удаляем недействительный токен
        localStorage.removeItem('token');
        Cookies.remove('token');
        dispatch({ type: 'SET_ERROR' });
      }
    };

    checkAuth();
  }, []);

  // Функция входа
  const login = async (credentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await authAPI.login(credentials);
      const { user, token } = response.data.data;
      
      console.log('Login response user:', user);
      console.log('User role:', user?.role);
      
      // Сохраняем токен
      localStorage.setItem('token', token);
      Cookies.set('token', token, { expires: 7 });
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });

      dispatch({ type: 'SET_LOADING', payload: false });
      
      toast.success(i18n.t('auth.login_success'));
      return { success: true, user };
    } catch (error) {
      dispatch({ type: 'SET_ERROR' });
      const message = error.response?.data?.message || i18n.t('auth.login_error', 'Ошибка входа');
      const code = error.response?.data?.code;
      const status = error.response?.status;
      
      if (code === 'EMAIL_NOT_VERIFIED') {
        toast.error(i18n.t('auth.verify_email_prompt', 'Пожалуйста, подтвердите ваш email адрес. Проверьте вашу почту.'));
        return { success: false, error: message, code };
      }

      if (status === 401 || code === 'INVALID_CREDENTIALS') {
        const normalizedMessage = typeof message === 'string' ? message.trim() : '';
        const isTranslationKeyMessage =
          normalizedMessage === 'auth.invalidCredentials' ||
          normalizedMessage === 'auth.invalid_credentials';
        const localized = (!normalizedMessage || isTranslationKeyMessage)
          ? i18n.t('auth.invalid_credentials')
          : normalizedMessage;
        return { success: false, error: localized, code };
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция регистрации
  const register = async (userData) => {
    console.log('AuthContext register called with:', userData); // Лог входящих данных
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await authAPI.register(userData);
      
      console.log('AuthContext registration response:', response); // Лог ответа API
      
      dispatch({ type: 'SET_LOADING', payload: false });
      
      // Не показываем toast здесь, так как компонент сам покажет сообщение
      return { 
        success: true, 
        data: response.data.data,
        message: response.data.message 
      };
    } catch (error) {
      console.error('AuthContext registration error:', error); // Лог ошибки
      console.error('Error response:', error.response?.data); // Лог ответа с ошибкой
      
      dispatch({ type: 'SET_ERROR' });
      const message = error.response?.data?.message || 'Ошибка регистрации';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция выхода
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      Cookies.remove('token');
      dispatch({ type: 'LOGOUT' });
      toast.success('Вы вышли из системы');
    }
  };

  // Функция обновления профиля
  const updateProfile = async (userData) => {
    try {
      const response = await userAPI.updateProfile(userData);
      
      dispatch({
        type: 'UPDATE_USER',
        payload: response.data.data.user,
      });
      
      toast.success('Профиль обновлён');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка обновления профиля';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция сброса пароля
  const forgotPassword = async (email) => {
    try {
      await authAPI.forgotPassword(email);
      toast.success('Инструкции отправлены на email');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка сброса пароля';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция подтверждения нового пароля
  const resetPassword = async (token, password) => {
    try {
      await authAPI.resetPassword(token, password);
      toast.success('Пароль успешно изменён');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка изменения пароля';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция подтверждения email
  const verifyEmail = async (token) => {
    try {
      await authAPI.verifyEmail(token);
      toast.success('Email подтверждён успешно');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка подтверждения email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция подтверждения email по коду
  const verifyEmailCode = async (email, code) => {
    try {
      const response = await authAPI.verifyEmailCode(email, code);
      
      // Если в ответе есть токен, автоматически логиним пользователя
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data.user });
      }
      
      return { 
        success: true, 
        message: response.data.message,
        token: response.data.token,
        user: response.data.user
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка подтверждения email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Функция повторной отправки кода
  const resendVerificationCode = async (email) => {
    try {
      const response = await authAPI.resendVerificationCode(email);
      toast.success('Код отправлен повторно');
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка отправки кода';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    verifyEmailCode,
    resendVerificationCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
export { AuthContext };
